<script lang="ts">
	import { onMount } from "svelte";
	import {
		shouldLoadHubSpotScript,
		shouldHonorDoNotTrack,
		getConsentCommands,
		type HubSpotConsentState,
	} from "#lib/hubspotConsent.js";

	// HubSpot Consent API integration
	// https://developers.hubspot.com/docs/api-reference/latest/account/settings/consent-banner/consent-banner-api
	let {
		alwaysLoad = false, // Load script immediately with necessary-only consent
		functionalAccepted = false, // Cookie level 2 (Funksjonelle)
		analyticsAccepted = false, // Cookie level 3 (Statistikk)
		marketingAccepted = false, // Cookie level 4 (Markedsføring)
	}: {
		alwaysLoad?: boolean;
		functionalAccepted?: boolean;
		analyticsAccepted?: boolean;
		marketingAccepted?: boolean;
	} = $props();

	// addHsScript drives <svelte:head> rendering, so it must be reactive.
	// One-way latch: once true, stays true for the client session.
	let addHsScript = $state(false);
	let onMountInitialization = $state(false);
	// Set in onMount based on browser's GPC/DNT signal. When true, we force
	// effectiveAlwaysLoad to false so HubSpot won't load until consent is given.
	let userOptedOut = $state(false);
	// Tracks whether consent has ever been granted in this session, so we can
	// send revokeCookieConsent on withdrawal. Read only inside the effect, so
	// kept as a plain (non-reactive) variable.
	let hasGrantedConsent = false;
	const consent: HubSpotConsentState = $derived({
		functionalAccepted,
		analyticsAccepted,
		marketingAccepted,
	});
	// alwaysLoad prop is the caller's preference; override to false if the user
	// has globally opted out via GPC or DNT.
	const effectiveAlwaysLoad = $derived(alwaysLoad && !userOptedOut);

	// Initialize _hsp command queue and disable HubSpot's native cookie banner
	// We use our own cookie consent UI instead
	onMount(() => {
		window._hsp = window._hsp || [];
		window.disableHubSpotCookieBanner = true;
		userOptedOut = shouldHonorDoNotTrack(navigator);
		onMountInitialization = true;
	});

	$effect(() => {
		if (!onMountInitialization || !window._hsp) return;

		const consentGiven = shouldLoadHubSpotScript(consent);

		if ((effectiveAlwaysLoad || consentGiven) && !addHsScript) addHsScript = true;
		if (consentGiven) hasGrantedConsent = true;

		for (const command of getConsentCommands(consent, hasGrantedConsent, {
			alwaysLoad: effectiveAlwaysLoad,
		})) {
			window._hsp.push(command);
		}
	});
</script>

<svelte:head>
	{#if addHsScript}
		<script
			type="text/javascript"
			id="hs-script-loader"
			async
			defer
			src="//js-eu1.hs-scripts.com/146569496.js"
		></script>
	{/if}
</svelte:head>
